import { Alert, Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCampaigns } from "@/hooks/useCampaigns";
import { usePermissions } from "@/hooks/usePermissions";

/**
 * "Your campaign's payment card was declined" — the client's own signal, from the FIRST failure.
 *
 * Not from the third. A client whose card has been declined has a problem the moment it happens:
 * the retry may fix it, and it may equally be an expired card that no number of retries will ever
 * clear. Waiting until collection has stopped means the first they hear of it is after their
 * campaign has already stopped funding — and a decline they were never told about is exactly how a
 * recipient ends up finding out first.
 *
 * The wording separates the two states rather than the banner's existence doing it: still retrying
 * reads as "we are trying again, and you can fix it now if you want"; stopped reads as "nothing
 * further happens until you act".
 *
 * THE CLIENT'S OWN ORGANISATION, AND NOBODY ELSE'S (Jayanth, 2026-09-14). GFG staff hold
 * unrestricted scope, so the campaigns list hands them every client's failing campaign — and a
 * banner telling a GFG admin that some other company's card was declined is both noise and somebody
 * else's business. Staff have the Actions dashboard, which is the surface built for it.
 *
 * It also requires `edit CAMPAIGN`: an alarm shown to someone who cannot act on it is an alarm with
 * nowhere to go — the card screen is guarded on the same capability, so the button would bounce
 * them straight back.
 */
export default function FundingBanner() {
  const navigate = useNavigate();
  const { isGfg, can } = usePermissions();
  const { data: campaigns } = useCampaigns();

  const failing = campaigns?.filter((c) => (c.topup?.failedAttempts ?? 0) > 0) ?? [];
  if (isGfg || !can("edit", "CAMPAIGN") || failing.length === 0) return null;

  // The worst one leads: a campaign that has stopped collecting outranks one still retrying.
  const first = failing.find((c) => c.topup?.stopped) ?? failing[0];
  if (!first) return null;

  const stopped = first.topup?.stopped === true;
  const others = failing.length - 1;

  return (
    // NO horizontal padding of its own. This renders inside the app shell's <main>, which already
    // carries `--app-main-padding`, and the routed page's content sits flush against that — so any
    // px here inset the banner further than the cards beneath it (Judd, 2026-09-14). Only the gap
    // below is ours: without it the banner reads as part of the first card rather than over them.
    <Box sx={{ pb: 3 }}>
      <Alert
        // Good Wine for both states (Judd, 2026-09-14). The theme maps error.main to it, and the
        // sentence — "we will try again" vs "nothing happens until you act" — is what separates a
        // decline being retried from one that has stopped. A second colour said it less clearly.
        severity="error"
        sx={{
          fontSize: 13,
          // The house alert shape: a thick Good Wine rule down the left against the wine tint,
          // matching the error banners elsewhere in the app rather than MUI's default.
          bgcolor: "var(--color-good-wine-bg)",
          borderLeft: "6px solid var(--color-good-wine)",
          borderRadius: "var(--radius-card, 8px)",
          alignItems: "center",
        }}
        action={
          // A filled button, not a text one. `color="inherit"` rendered it as the same wine as the
          // sentence beside it, so the one thing on the banner a client is meant to DO looked like
          // a heading. It is the only action here, so it can afford to be the loudest thing on it.
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate(`/campaigns/${first.id}/payment-card`)}
            sx={{
              bgcolor: "var(--color-good-wine)",
              color: "var(--color-white)",
              whiteSpace: "nowrap",
              px: 2,
              "&:hover": { bgcolor: "var(--color-good-wine-2)" },
            }}
          >
            Fix payment card
          </Button>
        }
      >
        {stopped
          ? `Funding suspended for “${first.name}”. Repeated attempts to charge the payment card on file were declined. No additional credits will be added to this campaign until a valid payment card is provided. Credits already allocated remain available to recipients.`
          : `Payment declined for “${first.name}”. We were unable to charge the payment card on file. The charge will be reattempted automatically; to resume funding immediately, update the payment card.`}
        {others > 0 ? ` ${others} other campaign${others === 1 ? " is" : "s are"} affected.` : ""}
      </Alert>
    </Box>
  );
}
